import type { ColumnDef } from '@tanstack/react-table';
import { createActionsColumn, createSerialColumn } from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { HighlightText } from '@/components/common/HighlightText';
import { RowEditDeleteButtons } from '@/components/common/RowActions';
import { formatDisplayDate } from '@/utils/date';
import type { LimitType } from '../types/limitType.types';

interface LimitTypeColumnActions {
  onEdit: (limitType: LimitType) => void;
}

const formatValue = (value: number | null): string =>
  value === null ? '—' : value.toLocaleString();

export function getLimitTypeColumns(
  searchText?: string,
  actions?: LimitTypeColumnActions,
): ColumnDef<LimitType, unknown>[] {
  return [
    createSerialColumn<LimitType>(),
    {
      accessorKey: 'name',
      header: 'Limit Type',
      cell: ({ row }) => (
        <span className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </span>
      ),
    },
    {
      accessorKey: 'lowValue',
      header: 'Low Value',
      cell: ({ row }) => <span className="tabular-nums">{formatValue(row.original.lowValue)}</span>,
    },
    {
      accessorKey: 'highValue',
      header: 'High Value',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatValue(row.original.highValue)}</span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Last Updated',
      cell: ({ row }) => formatDisplayDate(row.original.updatedAt),
    },
    ...(actions
      ? [
          createActionsColumn<LimitType>((limitType) => (
            <RowEditDeleteButtons
              item={limitType}
              itemName={limitType.name}
              onEdit={actions.onEdit}
            />
          )),
        ]
      : []),
  ];
}
